import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';

import Icon from '../../components/AppIcon';
import BackButton from '../../components/ui/BackButton';
import Select from '../../components/ui/Select';
import { snagService } from '../../services/snagService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getDefaultDashboard } from '../../utils/roleAccess';
import SnagHeader from './components/SnagHeader';
import SnagPhotoGallery from './components/SnagPhotoGallery';
import SnagActivityTimeline from './components/SnagActivityTimeline';
import SnagResolutionPanel from './components/SnagResolutionPanel';
import { useLanguage } from '../../contexts/LanguageContext';
import NotesFeed from '../../components/ui/NotesFeed';

const SnagDetailView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { snagId: snagIdParam } = useParams();
  const snagId = snagIdParam || searchParams?.get('id') || searchParams?.get('snagId');
  const { userProfile } = useAuth();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [snag, setSnag] = useState(null);
  const [comments, setComments] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showResolutionPanel, setShowResolutionPanel] = useState(false);
  const [error, setError] = useState('');
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [reassigning, setReassigning] = useState(false);

  const canEdit = ['admin', 'site_manager', 'worker']?.includes(userProfile?.role);
  const canReassign = ['admin', 'site_manager']?.includes(userProfile?.role);

  const breadcrumbItems = [
    { label: t('home'), href: getDefaultDashboard(userProfile?.role) },
    { label: t('snags'), href: '/snags' },
    { label: snag?.title || t('snagDetail') }
  ];

  useEffect(() => {
    if (!snagId) {
      navigate('/snags', { replace: true });
      return;
    }
    loadData();
    loadAssignableUsers();
  }, [snagId]);

  const loadAssignableUsers = async () => {
    try {
      // Include pending-invite users (is_active=false) so admins can pre-assign.
      // FK constraint guarantees the referenced profile exists.
      // jyyb8AfJ — exclude admin-deactivated users from the reassign picker.
      const { data } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, role, is_active')
        ?.in('role', ['worker', 'site_manager'])
        ?.is('deactivated_at', null)
        ?.order('full_name');
      if (data) setAssignableUsers(data);
    } catch (err) {
      console.error('Failed to load assignable users:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [snagResult, companyResult, commentsResult] = await Promise.all([
        snagService?.getSnagById(snagId),
        companyService?.getCompanyByAdminUser(),
        snagService?.getSnagComments(snagId)
      ]);

      if (snagResult?.data) setSnag(snagResult?.data);
      else navigate('/snags', { replace: true });

      if (companyResult?.data) setCompanyId(companyResult?.data?.id);
      if (commentsResult?.data) setComments(commentsResult?.data);
    } catch (err) {
      console.error('Error loading snag:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === 'resolved') {
      setShowResolutionPanel(true);
      return;
    }
    setUpdating(true);
    const result = await snagService?.updateSnag(snagId, { status: newStatus });
    if (result?.data) setSnag(result?.data);
    else setError(result?.error?.message);
    setUpdating(false);
  };

  const handleConfirmResolve = async (resolutionNotes) => {
    setUpdating(true);
    const result = await snagService?.updateSnag(snagId, {
      status: 'resolved',
      resolutionNotes
    });
    if (result?.data) {
      setSnag(result?.data);
      setShowResolutionPanel(false);
    } else {
      setError(result?.error?.message);
    }
    setUpdating(false);
  };

  const handleReassign = async (newUserId) => {
    setReassigning(true);
    const result = await snagService?.updateSnag(snagId, { assignedTo: newUserId || null });
    if (result?.data) setSnag(result?.data);
    else setError(result?.error?.message || 'Failed to reassign snag');
    setReassigning(false);
  };

  const handleCommentAdded = (comment) => {
    setComments(prev => [...prev, comment]);
  };

  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    ...assignableUsers?.map(u => ({
      value: u?.id,
      label: `${u?.full_name}${u?.role === 'site_manager' ? ' (Site Manager)' : ' (Worker)'}`
    }))
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <Icon name="Loader2" size={48} className="text-accent animate-spin" />
        </div>
      </div>
    );
  }

  if (!snag) return null;

  return (
    <>
      <Helmet>
        <title>{snag?.title} - Snag Detail - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <BackButton fallback="/snags" />
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-6">
              <SnagHeader
                snag={snag}
                onStatusChange={handleStatusChange}
                canEdit={canEdit}
                updating={updating}
              />

              {showResolutionPanel && (
                <SnagResolutionPanel
                  onConfirmResolve={handleConfirmResolve}
                  onCancel={() => setShowResolutionPanel(false)}
                  updating={updating}
                />
              )}

              {/* Description */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="FileText" size={18} />
                  {t('description')}
                </h3>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{snag?.description}</p>
              </div>

              <SnagPhotoGallery photoPaths={snag?.photo_paths || []} />

              {/* Notes Feed */}
              {companyId && (
                <NotesFeed
                  entityType="snag"
                  entityId={snag?.id}
                  companyId={companyId}
                  title="Snag Notes"
                />
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Snag details */}
              <div className="bg-card border border-border rounded-lg p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4">{t('snagDetails')}</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">{t('site')}</dt>
                    <dd className="text-sm font-medium text-foreground mt-0.5">{snag?.sites?.site_name}</dd>
                  </div>
                  {snag?.location && (
                    <div>
                      <dt className="text-xs text-muted-foreground">{t('location')}</dt>
                      <dd className="text-sm font-medium text-foreground mt-0.5">{snag?.location}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-xs text-muted-foreground">{t('reportedBy')}</dt>
                    <dd className="text-sm font-medium text-foreground mt-0.5">{snag?.user_profiles?.full_name || t('unknown')}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground mb-1">{t('assignedTo')}</dt>
                    {canReassign ? (
                      <div className="relative">
                        <Select
                          options={assigneeOptions}
                          value={snag?.assigned_to || ''}
                          onChange={handleReassign}
                          disabled={reassigning}
                        />
                        {reassigning && (
                          <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded">
                            <Icon name="Loader2" size={14} className="animate-spin text-accent" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <dd className="text-sm font-medium text-foreground">{snag?.assigned_user?.full_name || t('unassigned')}</dd>
                    )}
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">{t('created')}</dt>
                    <dd className="text-sm font-medium text-foreground mt-0.5">
                      {new Date(snag?.created_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">{t('photos')}</dt>
                    <dd className="text-sm font-medium text-foreground mt-0.5">{snag?.photo_paths?.length || 0}</dd>
                  </div>
                </dl>
              </div>

              <SnagActivityTimeline
                snag={snag}
                comments={comments}
                onCommentAdded={handleCommentAdded}
                companyId={companyId}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default SnagDetailView;
